import interpolateComponents from 'interpolate-components';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { usePaymentInputs } from 'react-payment-inputs';
import images from 'react-payment-inputs/images';

import Box from 'components/Box';
import { ErrorContainer, Label } from 'components/Form/Field';
import FormContext from 'components/Form/FormContext';
import { AnimatedCheckmark } from 'components/Icon';
import { Image } from 'components/Image';
import { LegacyInput } from 'components/LegacyInput';
import { BodyMicro, Underline } from 'components/Text';
import Tooltip from 'components/Tooltip';
import { colors } from 'theme';

const ERROR_MESSAGES = {
  emptyCardNumber: getText('The card number is required'),
  invalidCardNumber: getText('The card number does not appear to be valid'),
  emptyExpiryDate: getText('Expiry date is required'),
  monthOutOfRange: getText('The month must be between 01 and 12'),
  yearOutOfRange: getText('The year should not be in the past'),
  dateOutOfRange: getText('The date should not be in the past'),
  invalidExpiryDate: getText('The date is invalid'),
  emptyCVC: getText('The CVC is required'),
  invalidCVC: getText('The CVC is invalid'),
};
type InputName = 'cardNumber' | 'cvc' | 'expiryDate';
const FIELD_NAMES: Array<InputName> = ['cardNumber', 'expiryDate', 'cvc'];

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const CardImage = ({ isAmex }: { isAmex?: boolean }) => (
  <Box
    component={Image}
    src={isAmex ? '/static/images/cc-amex.svg' : '/static/images/cc-other.svg'}
  />
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const CardTooltip = ({
  cardType,
}: {
  cardType: {
    type: string;
  };
}) =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-nested-ternary
  !cardType ? (
    <>
      <BodyMicro lineHeight={1.25} textAlign="left">
        {interpolateComponents({
          mixedString: getText(
            'For Visa, MasterCard and Discover, the {{b}}3 digits{{/b}} on the {{u}}back{{/u}} of your card (left).'
          ),
          components: {
            b: <strong />,
            u: <Underline />,
          },
        })}
      </BodyMicro>
      <BodyMicro lineHeight={1.25} textAlign="left" mt="8px">
        {interpolateComponents({
          mixedString: getText(
            'For American Express, the {{b}}4 digits{{/b}} on the {{u}}front{{/u}} of your card (right).'
          ),
          components: {
            b: <strong />,
            u: <Underline />,
          },
        })}
      </BodyMicro>
      <Box display="flex" mx="-4px" mt={18}>
        <Box width={1 / 2} px="4px">
          <CardImage />
        </Box>
        <Box width={1 / 2} px="4px">
          <CardImage isAmex />
        </Box>
      </Box>
    </>
  ) : cardType.type === 'amex' ? (
    <>
      <BodyMicro lineHeight={1.25} mb={12}>
        {interpolateComponents({
          mixedString: getText(
            'The {{b}}4 digits{{/b}} on the {{u}}front{{/u}} of your card'
          ),
          components: {
            b: <strong />,
            u: <Underline />,
          },
        })}
      </BodyMicro>
      <CardImage isAmex />
    </>
  ) : (
    <>
      <BodyMicro lineHeight={1.25} mb={12}>
        {interpolateComponents({
          mixedString: getText(
            'The {{b}}3 digits{{/b}} on the {{u}}back{{/u}} of your card'
          ),
          components: {
            b: <strong />,
            u: <Underline />,
          },
        })}
      </BodyMicro>
      <CardImage />
    </>
  );

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const Error = ({
  errors,
  apiErrors,
  hasError,
  name,
}: {
  errors: Record<InputName, string>;
  apiErrors: Record<InputName, Array<string> | null | undefined>;
  hasError: (arg0: InputName) => boolean;
  name: InputName;
}) =>
  hasError(name) && (errors[name] || apiErrors[name]) ? (
    <ErrorContainer>
      <div>
        {apiErrors[name] && apiErrors[name].length > 0
          ? apiErrors[name].map((error) => <div key={error}>{error}</div>)
          : errors[name]}
      </div>
    </ErrorContainer>
  ) : null;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const CreditCardField = ({
  expiryOnly,
  wrap,
  disabled,
  ...props
}: {
  expiryOnly?: boolean;
  wrap?: boolean;
  disabled?: boolean;
  'data-qa-id'?: string;
}) => {
  const {
    updateValues,
    getValue,
    errors,
    fieldQaIdPrefix,
    updateSubmitDisabled,
  } = useContext(FormContext) || {};
  const qaId = props['data-qa-id'] || fieldQaIdPrefix || 'cc';
  const {
    getCardImageProps,
    getCardNumberProps,
    getExpiryDateProps,
    getCVCProps,
    meta: { cardType, touchedInputs, erroredInputs, error },
  } = usePaymentInputs({
    errorMessages: ERROR_MESSAGES,
  });
  const [apiErrors, setApiErrors] = useState({});
  useEffect(() => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newApiErrors: Record<string, any> = FIELD_NAMES.reduce(
      (acc, name) => {
        const errorArray = [];

        switch (name) {
          case 'cvc':
            if (errors.cvc) {
              errorArray.push(errors.cvc);
            }

            break;

          case 'expiryDate':
            if (errors.expMonth) {
              errorArray.push(errors.expMonth);
            }

            if (errors.expYear) {
              errorArray.push(errors.expYear);
            }

            break;

          case 'cardNumber':
          default:
            if (errors.number) {
              errorArray.push(errors.number);
            }

            break;
        }

        return { ...acc, [name as string]: errorArray };
      },
      {}
    );
    setApiErrors(newApiErrors);
  }, [errors.cvc, errors.expMonth, errors.expYear, errors.number]);
  const codeLength = cardType && cardType.code ? cardType.code.length : '3';
  const cardNumber = getValue('number') ? String(getValue('number')) : null;
  const cvc = getValue('cvc') ? String(getValue('cvc')) : null;
  const expMonth = getValue('expMonth') ? String(getValue('expMonth')) : null;
  const expYear = getValue('expYear') ? String(getValue('expYear')) : null;
  useEffect(() => {
    setApiErrors({});
  }, [cardNumber, cvc, expMonth, expYear]);

  const onExpiryChange = (e) => {
    const monthYear = e.target.value.split('/');
    const month = monthYear[0].trim();
    const year = monthYear[1] ? monthYear[1].trim() : '';
    const expiryDate = new Date(`${month}/01/${year}`);
    updateValues({
      expMonth: expiryDate.getMonth() + 1,
      expYear: expiryDate.getFullYear(),
    });
  };

  const hasError = (name) =>
    (apiErrors[name] && apiErrors[name].length > 0) ||
    (touchedInputs[name] && erroredInputs[name]);

  const isValid = useCallback(
    (name) =>
      !(erroredInputs[name] && erroredInputs[name].length > 0) &&
      (() => {
        switch (name) {
          case 'cvc':
            return cvc && codeLength && cvc.length === codeLength;

          case 'expiryDate':
            return (
              expMonth &&
              parseInt(expMonth, 10) >= 1 &&
              parseInt(expMonth, 10) <= 12 &&
              expYear &&
              expYear.length === 4
            );

          case 'cardNumber':
          default:
            return !!cardType;
        }
      })(),
    [cardType, codeLength, cvc, erroredInputs, expMonth, expYear]
  );
  const allValid =
    FIELD_NAMES.reduce((acc, name) => acc && isValid(name), true) && !error;
  useEffect(() => {
    updateSubmitDisabled(
      (!expiryOnly &&
        (!isValid('expiryDate') ||
          !(cardNumber && cardNumber.length > 0) ||
          !(cvc && cvc.length >= 3))) ||
        (!!expiryOnly && !isValid('expiryDate'))
    );
  }, [allValid, cardNumber, cvc, expiryOnly, isValid, updateSubmitDisabled]);
  const numberField = (
    <>
      <Label htmlFor={`${qaId}-number`}>{getText('Card Number')}</Label>
      <LegacyInput
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getCardNumberProps({
          onChange: (e) =>
            updateValues({
              number: e.target.value.replace(/\s/g, ''),
            }),
        })}
        placeholder="1111 2222 3333 4444"
        id={`${qaId}-card-number`}
        data-qa-id={`${qaId}-number`}
        required
        hasError={hasError('cardNumber')}
        isValid={isValid('cardNumber')}
        pr={52}
        disabled={disabled}
      />
      <Error
        hasError={hasError}
        errors={erroredInputs}
        // @ts-expect-error ts-migrate(2739) FIXME: Type '{}' is missing the following properties from... Remove this comment to see the full error message
        apiErrors={apiErrors}
        name="cardNumber"
      />
      <Box
        position="absolute"
        top={[76, null, 79]}
        right={21}
        transform="translateY(-50%)"
      >
        <svg
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...getCardImageProps({
            images,
          })}
        />
      </Box>
    </>
  );
  const expiryDateField = (
    <>
      <Label htmlFor={`${qaId}-expiry-date`} noWrap={expiryOnly}>
        {expiryOnly
          ? getText('Please provide the expiration date')
          : getText('Expiry Date')}
      </Label>
      <LegacyInput
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getExpiryDateProps({
          onChange: onExpiryChange,
        })}
        id={`${qaId}-expiry-date`}
        data-qa-id={`${qaId}-expiry-date`}
        required
        hasError={hasError('expiryDate')}
        isValid={isValid('expiryDate')}
        disabled={disabled}
      />
      <Error
        hasError={hasError}
        errors={erroredInputs}
        // @ts-expect-error ts-migrate(2322) FIXME: Type '{}' is not assignable to type 'Record<InputN... Remove this comment to see the full error message
        apiErrors={apiErrors}
        name="expiryDate"
      />
    </>
  );
  const cvcField = (
    <>
      <Label htmlFor={`${qaId}-cvc`}>
        <Box display="flex" alignItems="center">
          {getText('CVC')}
          <Box ml="4px" verticalAlign="middle">
            <Tooltip disabled={disabled}>
              <Box textAlign="center">
                <CardTooltip cardType={cardType} />
              </Box>
            </Tooltip>
          </Box>
        </Box>
      </Label>
      <LegacyInput
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getCVCProps({
          onChange: (e) =>
            updateValues({
              cvc: e.target.value
                .replace(/[^0-9]/g, '')
                .slice(0, cardType ? codeLength : 3),
            }),
        })}
        placeholder={cardType && codeLength === 4 ? '1234' : '123'}
        value={getValue('cvc')}
        id={`${qaId}-cvc`}
        data-qa-id={`${qaId}-cvc`}
        required
        hasError={hasError('cvc')}
        isValid={isValid('cvc')}
        disabled={disabled}
      />
      <Error
        hasError={hasError}
        errors={erroredInputs}
        // @ts-expect-error ts-migrate(2322) FIXME: Type '{}' is not assignable to type 'Record<InputN... Remove this comment to see the full error message
        apiErrors={apiErrors}
        name="cvc"
      />
      {allValid && (
        <Box
          position="absolute"
          top={76}
          right={-22}
          transform="translateY(-50%)"
          display={['none', null, 'flex']}
          alignItems="center"
          justifyContent="center"
          width={20}
          height={20}
          borderRadius="50%"
          bg={colors.turtleGreen}
        >
          <AnimatedCheckmark
            width="10px"
            height="10px"
            stroke="white"
            strokeWidth="3px"
            delay={0}
          />
        </Box>
      )}
    </>
  );
  return expiryOnly ? (
    <Box mb={30} width={200}>
      {expiryDateField}
    </Box>
  ) : (
    <Box
      display="flex"
      flexWrap="wrap"
      m="-8px"
      mb={22}
      data-qa-id={`${qaId}-credit-card`}
    >
      <Box position="relative" width={wrap ? 1 : [1, null, 1 / 2]} p="8px">
        {numberField}
      </Box>
      <Box
        position="relative"
        width={wrap ? [1, null, 1 / 2] : [1 / 2, null, 1 / 4]}
        p="8px"
      >
        {expiryDateField}
      </Box>
      <Box
        position="relative"
        width={wrap ? [1, null, 1 / 2] : [1 / 2, null, 1 / 4]}
        p="8px"
      >
        {cvcField}
      </Box>
    </Box>
  );
};

export default CreditCardField;
